/**
 * DesignThumb — a themed SVG mock of a design, drawn from the design's own
 * theme colours + its block sequence so each thumbnail looks distinct and
 * reflects the real layout. No external image assets.
 */
import React from 'react';

const rc = (t, key, fallback) => (t && t[key]) || fallback;

// A tiny stack of block "rows" so the thumb hints at the real layout.
const rowFor = (type, t, y) => {
  const primary = rc(t, 'primary', '#FF3B6B');
  const secondary = rc(t, 'secondary', '#06D6A0');
  const accent = rc(t, 'accent', '#FFD166');
  const surface = rc(t, 'surface', '#FFFFFF');
  const text = rc(t, 'text', '#1E1E24');
  switch (type) {
    case 'hero':
      return (
        <g key={y}>
          <rect x="10" y={y} width="110" height="8" rx="3" fill={text} opacity="0.85" />
          <rect x="10" y={y + 13} width="80" height="5" rx="2" fill={text} opacity="0.4" />
          <rect x="10" y={y + 24} width="34" height="10" rx="5" fill={primary} />
          <rect x="150" y={y - 2} width="40" height="40" rx="5" fill={accent} opacity="0.85" />
        </g>
      );
    case 'marquee':
      return <rect key={y} x="0" y={y} width="200" height="9" fill={primary} opacity="0.9" />;
    case 'stats':
      return (
        <g key={y}>
          {[10, 58, 106, 154].map((x) => <rect key={x} x={x} y={y} width="36" height="18" rx="3" fill={surface} stroke={primary} strokeOpacity="0.3" />)}
        </g>
      );
    case 'features':
    case 'packages':
    case 'testimonials':
    case 'steps':
      return (
        <g key={y}>
          {[10, 74, 138].map((x, i) => <rect key={x} x={x} y={y} width="52" height="26" rx="4" fill={surface} stroke={i === 1 && type === 'packages' ? primary : text} strokeOpacity={i === 1 && type === 'packages' ? 0.9 : 0.12} />)}
        </g>
      );
    case 'gallery':
      return (
        <g key={y}>
          {[10, 58, 106, 154].map((x, i) => <rect key={x} x={x} y={y} width="36" height="24" rx="3" fill={[primary, secondary, accent, text][i % 4]} opacity="0.75" />)}
        </g>
      );
    case 'about':
      return (
        <g key={y}>
          <rect x="10" y={y} width="86" height="30" rx="4" fill={secondary} opacity="0.55" />
          <rect x="108" y={y + 3} width="82" height="6" rx="2" fill={text} opacity="0.7" />
          <rect x="108" y={y + 14} width="64" height="5" rx="2" fill={text} opacity="0.35" />
        </g>
      );
    case 'cta_banner':
      return <rect key={y} x="10" y={y} width="180" height="26" rx="6" fill={primary} opacity="0.9" />;
    case 'faq':
    case 'contact':
    case 'richtext':
      return (
        <g key={y}>
          <rect x="10" y={y} width="180" height="9" rx="3" fill={surface} stroke={text} strokeOpacity="0.12" />
          <rect x="10" y={y + 13} width="180" height="9" rx="3" fill={surface} stroke={text} strokeOpacity="0.12" />
        </g>
      );
    case 'logo_strip':
      return (
        <g key={y}>
          {[10, 52, 94, 136].map((x) => <rect key={x} x={x} y={y} width="30" height="10" rx="5" fill={primary} opacity="0.25" />)}
        </g>
      );
    default:
      return <rect key={y} x="10" y={y} width="180" height="10" rx="3" fill={text} opacity="0.12" />;
  }
};

export const DesignThumb = ({ theme, blocks = [], className = '' }) => {
  const bg = rc(theme, 'bg', '#FFFDF8');
  let y = 12;
  const rows = [];
  (blocks.length ? blocks : ['hero', 'features', 'packages', 'testimonials']).slice(0, 5).forEach((type) => {
    rows.push(rowFor(type, theme, y));
    y += type === 'marquee' ? 16 : type === 'cta_banner' ? 34 : 42;
  });
  return (
    <svg viewBox="0 0 200 200" className={`block w-full h-auto ${className}`} xmlns="http://www.w3.org/2000/svg">
      <rect width="200" height="200" fill={bg} />
      {rows}
    </svg>
  );
};

export default DesignThumb;
